import { areaTriangle, invertMat3, multiplyMat3, radians, transformMat3 } from "@/core/utils/math"
import { XYtoXYZ } from "@/core/utils/color-conversion"
import { mat3, vec2, vec3 } from "gl-matrix"
import { whiteBalanceToXY } from "./temperature"

export const illuminants = {
	A: [1.0985, 1.0, 0.35585],
	D50: [0.9642, 1.0, 0.8249],
	D65: [0.9504, 1.0, 1.0889],
} satisfies Record<string, vec3>

export interface Primaries {
	r: vec2
	g: vec2
	b: vec2
	w: vec2
}

export const primaries = {
	proPhoto: {
		r: [0.734699, 0.265301],
		g: [0.159597, 0.840403],
		b: [0.036598, 0.000105],
		w: [0.345704, 0.35854],
	} as Primaries,
}

// Bradford

// prettier-ignore
export const adaptionMatrixBradford: mat3 = [
	0.8951000, 0.2664000, -0.1614000,
	-0.7502000, 1.7135000, 0.0367000,
	0.0389000, -0.0685000, 1.0296000
]

// prettier-ignore
export const adaptionMatrixBradfordInv: mat3 = [
	0.9869929, -0.1470543, 0.1599627,
	0.4323053, 0.5183603, 0.0492912,
	-0.0085287, 0.0400428, 0.9684867
]

// CAT16

// prettier-ignore
// const adaptionMatrix: Mat3 = [
//   0.401288, 0.650173, -0.051461,
//   -0.250268, 1.204414,  0.045854,
//   -0.002079, 0.048952,  0.953127
// ]

// prettier-ignore
// const adaptionMatrixInv: Mat3 = [
//   1.862068, -1.011255,  0.149187,
//   0.38752 ,  0.621447, -0.008974,
//   -0.015841, -0.034123,  1.049964
// ]

// See: http://www.brucelindbloom.com/index.html?Eqn_ChromAdapt.html
export function getAdaptionMatrix(srcWhite: vec3, dstWhite: vec3) {
	const gains = getLMSGains(srcWhite, dstWhite)

	// prettier-ignore
	const scale: mat3 = [
		gains[0], 0, 0,
		0, gains[1], 0,
		0, 0, gains[2]
	]

	return multiplyMat3(multiplyMat3(adaptionMatrixBradfordInv, scale), adaptionMatrixBradford)
}

export function getLMSGains(srcWhite: vec3, dstWhite: vec3) {
	const [ps, ys, bs] = transformMat3(srcWhite, adaptionMatrixBradford)
	const [pd, yd, bd] = transformMat3(dstWhite, adaptionMatrixBradford)

	const gains: vec3 = [pd / ps, yd / ys, bd / bs]

	gains[0] = Math.max(0.1, Math.min(ps > 0 ? gains[0] : 10, 10))
	gains[1] = Math.max(0.1, Math.min(ys > 0 ? gains[1] : 10, 10))
	gains[2] = Math.max(0.1, Math.min(bs > 0 ? gains[2] : 10, 10))

	return gains
}

export function getWBGainData(options: {
	adjustedTemp: number
	adjustedTint: number
	asShotWhite: vec3
	adjustedWhite: vec3
}) {
	const { adjustedTemp, adjustedTint, asShotWhite, adjustedWhite } = options

	const mid = 20
	const size = 2 * mid + 1
	const gains = new Float32Array(size * size * 3)
	const [minTemp, maxTemp] = [2000, 25000]
	const [minTint, maxTint] = [-150, 150]

	for (let i = 0; i < size; i++) {
		for (let j = 0; j < size; j++) {
			let white: vec3
			if (i === mid && j === mid) {
				white = adjustedWhite
			} else {
				const temp =
					i < mid
						? ((adjustedTemp - minTemp) / mid) * i + minTemp
						: ((maxTemp - adjustedTemp) / mid) * (i - mid) + adjustedTemp

				const tint =
					j < mid
						? ((adjustedTint - minTint) / mid) * j + minTint
						: ((maxTint - adjustedTint) / mid) * (j - mid) + adjustedTint

				white = XYtoXYZ(whiteBalanceToXY([temp, tint]))
			}

			const gain = getLMSGains(white, asShotWhite)

			const index = 3 * (i * size + j)
			gains[index] = gain[0]
			gains[index + 1] = gain[1]
			gains[index + 2] = gain[2]
		}
	}

	return gains
}

export function adjustPrimaries(
	primaries: Primaries,
	adjustments: {
		redHue?: number
		redSat?: number
		greenHue?: number
		greenSat?: number
		blueHue?: number
		blueSat?: number
	},
): Primaries {
	let { r, g, b } = primaries
	const w = primaries.w

	// Copy primaries so we don't alter originals
	r = vec2.clone(r)
	g = vec2.clone(g)
	b = vec2.clone(b)

	// Set origin to white point
	vec2.sub(r, r, w)
	vec2.sub(g, g, w)
	vec2.sub(b, b, w)

	const initialArea = areaTriangle(r, g, b)

	// experimental finding the best scale for negative saturation
	const totalSat =
		(adjustments.redSat ?? 0) + (adjustments.greenSat ?? 0) + (adjustments.blueSat ?? 0)
	let negSatScale = 0.66
	if (totalSat < -1) {
		negSatScale += (totalSat + 1) * 0.03
	}
	adjustPrimarySat(r, adjustments.redSat ?? 0, negSatScale * 0.8)
	adjustPrimarySat(g, adjustments.greenSat ?? 0, negSatScale)
	adjustPrimarySat(b, adjustments.blueSat ?? 0, negSatScale)

	const targetArea = areaTriangle(r, g, b)

	// chroma adaptive scaling - less hue angle rotation for higher chroma, one can understand it as if the distance = r * angle is constant, so the angle is scaled by 1/r.
	// approximate the chroma by the area of the triangle, this gives closer-to-LR results than compute chroma from individual primaries
	// lower saturation, bigger triangle, higher chroma -> less hue angle scale; vice versa for higher saturation.
	const areaScale = targetArea / initialArea
	const hueScale = Math.min(1 / Math.sqrt(areaScale), 1.2)

	adjustPrimaryHue(r, adjustments.redHue ?? 0, [-15, 18], hueScale)
	adjustPrimaryScale([r, g, b], 0, targetArea)

	adjustPrimaryHue(g, adjustments.greenHue ?? 0, [-15, 15], hueScale)
	adjustPrimaryScale([r, g, b], 1, targetArea)

	adjustPrimaryHue(b, adjustments.blueHue ?? 0, [-18, 18], hueScale)
	adjustPrimaryScale([r, g, b], 2, targetArea)

	// Reset origin to white point
	vec2.add(r, r, w)
	vec2.add(g, g, w)
	vec2.add(b, b, w)

	return { r, g, b, w }
}

function adjustPrimaryHue(primary: vec2, hue: number, range: vec2, hueScale: number) {
	hue = hue > 0 ? hue * -range[1] : hue * range[0]

	const angle = radians(hue) * hueScale
	const cos = Math.cos(angle)
	const sin = Math.sin(angle)

	const [x, y] = primary

	primary[0] = x * cos - y * sin
	primary[1] = x * sin + y * cos
}

// scale primaries to match target area
function adjustPrimaryScale(primaries: [vec2, vec2, vec2], index: number, targetArea: number) {
	let area = areaTriangle(primaries[0], primaries[1], primaries[2])
	let iter = 0
	while (Math.abs(targetArea - area) > 0.001 && iter < 10) {
		const scale = (Math.sqrt(targetArea / area) - 1) * 2 + 1
		vec2.scale(primaries[index] as vec2, primaries[index] as vec2, scale)
		area = areaTriangle(primaries[0], primaries[1], primaries[2])
		iter++
	}
}

function adjustPrimarySat(primary: vec2, sat: number, negSatScale: number) {
	sat = sat > 0 ? sat * 0.4 : sat * negSatScale
	primary[0] /= 1 + sat
	primary[1] /= 1 + sat
}

export function primariesToMatrix(primaries: Primaries): mat3 {
	const { r, g, b, w } = primaries

	const XYZr = XYtoXYZ(r)
	const XYZg = XYtoXYZ(g)
	const XYZb = XYtoXYZ(b)
	const XYZw = XYtoXYZ(w)

	// prettier-ignore
	const RGB: mat3 = [
		XYZr[0], XYZg[0], XYZb[0],
		XYZr[1], XYZg[1], XYZb[1],
		XYZr[2], XYZg[2], XYZb[2]
	]

	const S = transformMat3(XYZw, invertMat3(RGB))

	// RGB to XYZ
	// prettier-ignore
	const M: mat3 = [
		S[0] * RGB[0], S[1] * RGB[1], S[2] * RGB[2],
		S[0] * RGB[3], S[1] * RGB[4], S[2] * RGB[5],
		S[0] * RGB[6], S[1] * RGB[7], S[2] * RGB[8]
	]

	// XYZ to RGB
	return invertMat3(M)
}
